import React, { Component } from 'react';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import helpers from 'helpers';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import { objectives as objectiveNameMap } from 'constants/facebook';
import { generateFooterData, generateHeaders, applyDefaultColumns } from 'helpers/tables';
import { insertBeforeItem } from 'helpers/utilities';
import { reducePCVData } from 'helpers/social';
import DefaultTable from 'components/atoms/tables/smarttables/DefaultTable';
import { stripJobNumber, capitalizeFirstLetter } from 'helpers/strings';

const DEFAULT_ACCESSORS = ['platform', 'name', 'objective', 'impressions', 'clicks', 'cpc', 'inline_link_clicks', 'cost_per_inline_link_click', 'ctr', 'conversions', 'conversion_rate', 'purchase_roas', 'spend'];

class SocialCampaignOverviewTable extends Component {
    constructor(props) {
        super(props);
        let columns;
        let data;

        if (props.hasData) {
            const table = SocialCampaignOverviewTable.createTableData(props.campaigns, props);
            columns = table.columns;
            data = table.data;
        }

        this.state = {
            data,
            columns,
            title: props.title,
            exportName: props.exportName,
            campaignParams: (this.props.campaigns || {}).params,
            cacheKey: props.cacheKey
        };
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps.campaigns, this.props.campaigns);
    }

    static createTableData(campaigns = {}, props) {
        const { campaignIdMap } = campaigns;
        const { showConversionRevenue, showSocialCpconv, showConversionTrends } = props;

        let keys = ['impressions', 'clicks', 'cpc', 'inline_link_clicks', 'cost_per_inline_link_click', 'ctr', 'conversions', 'cost_per_conversion', 'conversion_rate', 'purchase_roas', 'purchase_conversion_value', 'spend'];
        const data = Object.entries(campaignIdMap).map(c => ({
            name: stripJobNumber(c[1].name),
            ...campaigns[c[0]].statistics,
            objective: objectiveNameMap[c[1].objective],
            platform: c[1].platform
        }));
        const { hasROASData, sumPCVWithoutZeroes, sumSpendWithoutZeroes } = reducePCVData({ data });
        const defaultVisableCols = ['name', 'impressions', 'conversion_revenue', 'cost_per_conversion', 'purchase_conversion_value', 'spend'];
        // if no conversions, show clicks instead
        if (showConversionTrends) {
            defaultVisableCols.push('conversions');
        } else {
            defaultVisableCols.push('clicks');
        }

        let accessors = DEFAULT_ACCESSORS;
        if (!hasROASData) {
            accessors = DEFAULT_ACCESSORS.filter(key => key !== 'purchase_roas');
            keys = keys.filter(key => key !== 'purchase_roas');
        }

        if (showConversionRevenue && accessors.indexOf('purchase_conversion_value') === -1) {
            accessors = insertBeforeItem(accessors, 'spend', 'purchase_conversion_value');
        }

        if (showSocialCpconv && accessors.indexOf('cost_per_conversion') === -1) {
            accessors = insertBeforeItem(accessors, 'conversion_rate', 'cost_per_conversion');
        }

        const headers = generateHeaders({
            keys,
            footerData: generateFooterData({
                data,
                filteredKeys: ['name', 'objective'],
                extraData: { sumPCVWithoutZeroes, sumSpendWithoutZeroes }
            }),
            colOptions: {
                labelCode: props.entityObjectiveLabelCode
            },
            initial: {
                platform: {
                    Header: 'Platform',
                    accessor: 'platform',
                    Cell: row => <CellDiv value={capitalizeFirstLetter(row.value)} />,
                    Footer: (
                        <CellDiv value='' />
                    ),
                    filter: 'textColumnFilter'
                },
                name: {
                    Header: 'Campaign',
                    accessor: 'name',
                    Cell: row => <CellDiv value={row.value} />,
                    filter: 'textColumnFilter',
                    isIndexCol: true
                },
                objective: {
                    Header: 'Objective',
                    accessor: 'objective',
                    Cell: row => <CellDiv value={row.value} />,
                    Footer: (
                        <CellDiv value='' />
                    ),
                    maxWidth: '150',
                    filter: 'textColumnFilter'
                },
            }
        });
        const columns = applyDefaultColumns(
            accessors.map(accessor => headers[accessor]),
            defaultVisableCols
        );
        return { data, columns };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const nextCampaigns = nextProps.campaigns;
        const nextCampaignParams = nextCampaigns.params;
        const nextTitle = nextProps.title;
        const nextExportName = nextProps.exportName;
        let nextState = null;

        if (nextCampaignParams !== prevState.campaignParams) {
            const { data, columns } = SocialCampaignOverviewTable.createTableData(nextCampaigns, nextProps);

            nextState = {
                ...prevState,
                data,
                columns,
                title: nextTitle,
                exportName: nextExportName,
                campaignParams: nextCampaignParams,
            };
        }
        return nextState;
    }

    render() {
        const { data, title, exportName, columns, cacheKey } = this.state;
        return (
            <ContentTile padding={helpers.contentPadding('table')}>
                <ContentHeader tableData={data} exportName={exportName} type={'table'} allowExport={false}>{title}</ContentHeader>
                <DefaultTable
                    columns={columns}
                    data={data}
                    cacheKey={cacheKey}
                    globalSearchPlaceholder='Search'
                    globalSearch={false}
                    exportData
                    fileName={exportName}
                    exportName={exportName}
                    title={title}
                    pagination={data.length > RowsPerPageDefault}
                    aggregate={true}
                    defaultState={{
                        pageSize: RowsPerPageDefault,
                        sortBy: [
                            { id: 'name', desc: false },
                        ]
                    }}
                />
            </ContentTile>
        );
    }
}

SocialCampaignOverviewTable.propTypes = {
    title: PropTypes.string.isRequired,
    exportName: PropTypes.string.isRequired,
    campaigns: PropTypes.shape({
        hasData: PropTypes.bool,
        campaignIdMap: PropTypes.object.isRequired,
        params: PropTypes.object
    }).isRequired
};

export default SocialCampaignOverviewTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/SocialCampaignOverviewTable.js